import type { ComponentPropsWithRef, ElementType, ReactNode } from 'react';
import type { FieldValues, Path } from 'react-hook-form';

import { cn } from '@pesto/utils';
import React from 'react';

import type { Input } from '../../components/Input';

import { TextFieldWrapper } from '../../components/Forms/TextFieldWrapper';
import { InputWithOnChange } from '../../components/Input';
import { genericForwardRef } from '../../utils/composition';

type FormInputBaseProps<TFieldValues extends FieldValues> = {
  label?: ReactNode;
  helperText?: ReactNode;
  error?: string;
  description?: ReactNode;
  required?: boolean;
  wrapperClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  onChange?: (value: string) => void;
  id?: string;
  name?: Path<TFieldValues>;
  readOnly?: boolean;
  disabled?: boolean;
};

export type FormInputProps<
  TFieldValues extends FieldValues = FieldValues,
  T extends ElementType = typeof Input,
> = FormInputBaseProps<TFieldValues> & {
  as?: T;
} & Omit<ComponentPropsWithRef<T>, keyof FormInputBaseProps<TFieldValues>>;

const FormInputInner = <TFieldValues extends FieldValues = FieldValues, T extends ElementType = typeof Input>(
  {
    label,
    helperText,
    error,
    description,
    required,
    wrapperClassName,
    labelClassName,
    inputClassName,
    id: providedId,
    as,
    className,
    name,
    onChange,
    'aria-describedby': ariaDescribedBy,
    readOnly,
    disabled,
    ...props
  }: FormInputProps<TFieldValues, T> & { forwardedRef?: ComponentPropsWithRef<T>['ref'] },
  ref: ComponentPropsWithRef<T>['ref'],
) => {
  const id = providedId || `form-field-${name}`;
  const helperId = `${id}-helper`;
  const errorId = `${id}-error`;
  const descriptionId = `${id}-description`;

  const describedBy = [ariaDescribedBy, helperText && helperId, error && errorId, description && descriptionId]
    .filter(Boolean)
    .join(' ');

  const Component = as || InputWithOnChange;

  return (
    <TextFieldWrapper
      className={wrapperClassName}
      label={label}
      error={error}
      helperText={helperText}
      description={description}
      required={required}
      labelClassName={labelClassName}
      id={id}
      name={name}
      readOnly={readOnly}
    >
      <Component
        ref={ref}
        id={id}
        name={name}
        onChange={onChange}
        aria-describedby={describedBy || undefined}
        aria-invalid={!!error}
        aria-required={required}
        className={cn(error && 'border-destructive', inputClassName, className)}
        readOnly={readOnly}
        disabled={readOnly || disabled}
        {...props}
      />
    </TextFieldWrapper>
  );
};

// SAFETY: forwardRef only attaches the ref; props reach FormInputInner unchanged, so its generic signature holds.
export const FormInput = genericForwardRef(FormInputInner, 'FormInput') as <
  TFieldValues extends FieldValues = FieldValues,
  T extends ElementType = typeof Input,
>(
  props: FormInputProps<TFieldValues, T> & { ref?: ComponentPropsWithRef<T>['ref'] },
) => React.ReactElement | null;
